import DatePicker from 'components/base/DatePicker';
import React from 'react';
import { Col, Row } from 'react-bootstrap';
import { Form } from 'react-bootstrap';
import { useForm } from 'react-hook-form';
import axiosInstance from 'utils/axiosInterceptors';
import { useBranchContext } from 'providers/BranchProvider';
import { formatToUTCISO } from 'utils/formatToUTCISO';
import { toast } from 'react-toastify';
import { toastConfig } from 'utils/toastConfig';

type TSupplierPaymentDetails = {
  supplierPaymentMethod: string;
  accountDate: Date | string;
  supplierPaymentId: number;
  procurementInvoiceIds: (number | undefined)[];
  procurementReturnIds: (number | undefined)[];
  paymentNotes?: string;
};

export type TEditSupplierPaymentFormProps = {
  submitForm?: () => Promise<boolean | undefined>;
  defaultValues?: Partial<TSupplierPaymentDetails>;
};

const EditSupplierPaymentForm = React.forwardRef<
  TEditSupplierPaymentFormProps,
  TEditSupplierPaymentFormProps
>(({ defaultValues }, ref) => {
  const { selectedBranch } = useBranchContext();
  const { register, handleSubmit, setValue, watch, reset } =
    useForm<TSupplierPaymentDetails>({
      defaultValues: {
        supplierPaymentMethod: '',
        accountDate: new Date(),
        procurementInvoiceIds: [],
        procurementReturnIds: [],
        paymentNotes: '',
        ...defaultValues
      }
    });

  const accountDate = watch('accountDate');

  const onSubmit = async (data: TSupplierPaymentDetails) => {
    const formattedDate = formatToUTCISO(data.accountDate);
    const formattedPaymentDetails = {
      procurementInvoiceIds: data.procurementInvoiceIds,
      procurementReturnIds: data.procurementReturnIds,
      supplierPaymentMethod: data.supplierPaymentMethod,
      accountDate: formattedDate,
      paymentNotes: data.paymentNotes ?? ''
    };
    try {
      await axiosInstance.put(
        `/api/v1/${selectedBranch?.branchDomain}/supplierPayments/${defaultValues?.supplierPaymentId}`,
        formattedPaymentDetails
      );
      toast.success('Payment details submitted successfully!', {
        ...toastConfig,
        autoClose: 2000
      });
      return true;
    } catch (error) {
      console.error(error);
      toast.error('Failed to submit payment details. Please try again.');
      return false;
    }
  };

  React.useImperativeHandle(ref, () => ({
    submitForm: () =>
      new Promise<boolean>(resolve => {
        handleSubmit(async data => {
          const result = await onSubmit(data);
          resolve(result);
        })();
      })
  }));

  React.useEffect(() => {
    if (defaultValues) {
      reset(defaultValues);
    }
  }, [defaultValues, reset]);

  return (
    <>
      <Form className="w-100">
        <Row className="mb-3">
          <Form.Group as={Col} controlId="paymentMethod">
            <Form.Label>Payment Method</Form.Label>
            <Form.Check
              type="radio"
              id="checkRadio"
              label="Check"
              value="Check"
              {...register('supplierPaymentMethod')}
            />
            <Form.Check
              type="radio"
              id="cashRadio"
              label="Cash"
              value="Cash"
              {...register('supplierPaymentMethod')}
            />
            <Form.Check
              type="radio"
              id="bankTransferRadio"
              label="Bank Transfer"
              value="BankTransfer"
              {...register('supplierPaymentMethod')}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="accountDate">
            <Form.Label>Account Date</Form.Label>
            <DatePicker
              value={
                accountDate instanceof Date
                  ? accountDate
                  : new Date(accountDate)
              }
              onChange={date => setValue('accountDate', (date as Date[])[0])}
            />
          </Form.Group>
        </Row>
        <Row className="mb-3">
          <Form.Group as={Col} controlId="paymentNotes">
            <Form.Label>Remarks</Form.Label>
            <Form.Control
              as="textarea"
              rows={3}
              {...register('paymentNotes')}
            />
          </Form.Group>
        </Row>
      </Form>
    </>
  );
});

export default EditSupplierPaymentForm;
